import { gateProfit } from './gate';
import { transferEligible } from '../lib/transferFeed';
import { feedKey, TRANSFERS } from '../lib/feedContext';
/**
 * Сигнал о новой вилке: звук и уведомление браузера.
 *
 * Вилочник не смотрит в ленту непрерывно. Вкладка стоит на втором мониторе, а
 * он в это время вбивает ставку в конторе, курит или спит. Сигнал — это не
 * украшение: строка живёт секунды, и человек, узнавший о ней через минуту,
 * узнал о ней зря.
 *
 * Отсюда три правила, каждое из которых важнее самой функции.
 *
 * **Сигнал даётся по ЕГО профилю, а не по ленте.** Звенеть на всё подряд — это
 * способ добиться, чтобы звук выключили в первые пять минут и больше не
 * включали. Поэтому сюда приходят строки, УЖЕ прошедшие фильтры и порог денег:
 * фильтр по конторам и «беру от ₽» работают на сигнал бесплатно.
 *
 * **Первый кадр молчит.** Вкладка, открытая на полной ленте, иначе выдала бы
 * полсотни сигналов подряд — и человек справедливо решил бы, что сломано.
 * Первый кадр только запоминает, что в нём было.
 *
 * **Один сигнал на вилку.** Строка живёт минутами и переиздаётся каждым тактом;
 * без памяти по идентификаторам она звенела бы всё это время. Память чистится
 * по ушедшим строкам, иначе за сутки в ней осядут десятки тысяч ключей.
 *
 * Звук синтезируется, а не грузится файлом. Файл — это лишний запрос, который
 * может не успеть, и лишний путь в сборке, который может отвалиться под
 * префиксом /v2; короткий тон Web Audio не зависит ни от того, ни от другого.
 *
 * Громкость и тон живут на этой машине, не в шареном профиле: наушники одни,
 * сито ленты — общее. Порог «звать от» — в профиле, его читает лента.
 *
 * Фон вкладки. Chrome засыпает AudioContext, как только человек уходит на
 * контору в соседней вкладке. `resume()` без жеста в фоне часто не будит:
 * осциллятор стартует на застывших часах и молчит именно тогда, когда сигнал
 * нужен. Поэтому при включённом звуке держим тихий 20 кГц — вкладка «звучит»,
 * контекст не спит; если контекст всё же не running — тот же тон играет
 * через HTMLAudio (data-URI WAV). Колокольчик не будим: он спрятан, потому
 * что врал.
 */

import { useEffect, useRef } from 'react';
import { create } from 'zustand';

import { useSettings } from '../store/settings';
import { parked } from './parked';
import type { FeedRow } from './rows';
import { profitText, useShow } from './show';

export const ALARM_KEY = feedKey('forq.next.alarm');
const KEY = ALARM_KEY;

export const TONE_IDS = ['soft', 'plain', 'high', 'low', 'sharp'] as const;
export type ToneId = (typeof TONE_IDS)[number];

export const SCALE_IDS = ['flat', 'money'] as const;
export type ScaleId = (typeof SCALE_IDS)[number];

export const DEFAULT_VOLUME = 80;
export const DEFAULT_TONE: ToneId = 'plain';
export const DEFAULT_SCALE: ScaleId = 'flat';

/**
 * Таблица тонов: частота и длительность. Тест сверяет её, а не мок
 * осциллятора — в jsdom AudioContext часто нет вовсе.
 */
export interface ToneSpec {
  hz: readonly number[];
  gap: number;
  dur: number;
  type: OscillatorType;
  /** Пик при громкости 100 и «один тон». */
  peak: number;
}

export const TONES: Record<ToneId, ToneSpec> = {
  soft: { hz: [880, 1175], gap: 0.12, dur: 0.12, type: 'sine', peak: 0.12 },
  plain: { hz: [880, 1175], gap: 0.11, dur: 0.14, type: 'sine', peak: 0.36 },
  high: { hz: [1175, 1568], gap: 0.1, dur: 0.12, type: 'sine', peak: 0.32 },
  low: { hz: [392, 523], gap: 0.14, dur: 0.16, type: 'triangle', peak: 0.4 },
  sharp: { hz: [988, 1480], gap: 0.08, dur: 0.1, type: 'square', peak: 0.18 },
};

export function toneOf(id: unknown): ToneId {
  return typeof id === 'string' && (TONE_IDS as readonly string[]).includes(id)
    ? (id as ToneId)
    : DEFAULT_TONE;
}

export function scaleOf(id: unknown): ScaleId {
  return id === 'money' ? 'money' : DEFAULT_SCALE;
}

export function volumeOf(value: unknown): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : DEFAULT_VOLUME;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/**
 * Множитель «крупнее — громче»: от суммы в рублях, не от процента.
 * 0 ₽ → 0.7, 10 000 ₽ и выше → 1.35.
 */
export function moneyLoud(rub: number): number {
  const n = Number.isFinite(rub) ? Math.max(0, rub) : 0;
  if (n <= 0) return 0.7;
  return 0.7 + Math.min(1, n / 10000) * 0.65;
}

export function peakGain(opts: {
  volume: number;
  tone: ToneId;
  scale?: ScaleId;
  rub?: number;
}): number {
  const spec = TONES[toneOf(opts.tone)];
  const vol = volumeOf(opts.volume) / 100;
  const money = opts.scale === 'money' ? moneyLoud(opts.rub ?? 0) : 1;
  return spec.peak * vol * money;
}

export interface AlarmState {
  sound: boolean;
  notify: boolean;
  /** Отказ браузера в разрешении. Молча гасить переключатель нельзя. */
  denied: boolean;
  volume: number;
  tone: ToneId;
  scale: ScaleId;
  setSound: (on: boolean) => void;
  setNotify: (on: boolean) => Promise<void>;
  setVolume: (volume: number) => void;
  setTone: (tone: ToneId) => void;
  setScale: (scale: ScaleId) => void;
}

interface Saved {
  sound?: boolean;
  notify?: boolean;
  volume?: number;
  tone?: string;
  scale?: string;
}

function load(): Saved {
  try {
    const raw = localStorage.getItem(KEY);
    return raw === null ? {} : (JSON.parse(raw) as Saved);
  } catch {
    return {};
  }
}

export function alarmRecord(state: AlarmState): Saved {
  return { sound: state.sound, notify: state.notify, volume: state.volume, tone: state.tone, scale: state.scale };
}

const saved = load();

export const useAlarm = create<AlarmState>((set, get) => {
  const store = (): void => {
    try {
      localStorage.setItem(
        KEY,
        JSON.stringify(alarmRecord(get())),
      );
    } catch {
      // Приватный режим — не повод падать.
    }
  };
  return {
    sound: saved.sound === true,
    // Уведомления не восстанавливаются включёнными: разрешение живёт в
    // браузере, а не у нас, и его могли отозвать между сессиями. Настоящее
    // состояние выясняется при включении.
    notify: !parked.browserPush && saved.notify === true && permission() === 'granted',
    denied: permission() === 'denied',
    volume: volumeOf(saved.volume),
    tone: toneOf(saved.tone),
    scale: scaleOf(saved.scale),

    setSound: (sound) => {
      set({ sound });
      store();
      // Проверочный тон сразу при включении: беззвучный переключатель, который
      // «включён», но не слышен из-за политики автовоспроизведения, хуже
      // выключенного — на него надеются. Жест включения ещё и будит контекст,
      // чтобы потом пищать из фона.
      if (sound) {
        beep();
        armSound();
      } else {
        disarmSound();
      }
    },

    setVolume: (volume) => {
      const next = volumeOf(volume);
      set({ volume: next });
      store();
      if (get().sound && next > 0) armSound();
      else if (next <= 0) disarmSound();
    },

    setTone: (tone) => {
      set({ tone: toneOf(tone) });
      store();
    },

    setScale: (scale) => {
      set({ scale: scaleOf(scale) });
      store();
    },

    setNotify: async (on) => {
      if (parked.browserPush) {
        set({ notify: false });
        store();
        return;
      }
      if (!on) {
        set({ notify: false });
        store();
        return;
      }
      const granted = await ask();
      set({ notify: granted, denied: !granted });
      store();
    },
  };
});

function permission(): string {
  try {
    return typeof Notification === 'undefined' ? 'default' : Notification.permission;
  } catch {
    return 'default';
  }
}

async function ask(): Promise<boolean> {
  try {
    if (typeof Notification === 'undefined') return false;
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;
    return (await Notification.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}

let audio: AudioContext | null = null;
let hold: OscillatorNode | null = null;
let holdGain: GainNode | null = null;
let watching = false;
let elem: HTMLAudioElement | null = null;
const wavCache = new Map<string, string>();

export interface BeepOpts {
  volume?: number;
  tone?: ToneId;
  scale?: ScaleId;
  rub?: number;
}

function audioCtor(): (new () => AudioContext) | undefined {
  if (typeof window === 'undefined') return undefined;
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  );
}

function context(): AudioContext | null {
  const Ctor = audioCtor();
  if (Ctor === undefined) return null;
  audio ??= new Ctor();
  return audio;
}

/** Держать вкладку «звучащей», только если человек включил звук и не крутит ноль. */
export function shouldHold(sound: boolean, volume: unknown): boolean {
  return sound && volumeOf(volume) > 0;
}

function holdOn(): void {
  const ctx = audio;
  if (!ctx || ctx.state !== 'running' || hold !== null) return;
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    // Chrome не считает полностью тихий поток «звуком» и всё равно засыпает
    // контекст. 20 кГц почти никто не слышит; иконка динамика на вкладке —
    // как раз знак, что сигнал вооружён.
    osc.type = 'sine';
    osc.frequency.value = 20000;
    gain.gain.value = 0.0008;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    hold = osc;
    holdGain = gain;
  } catch {
    hold = null;
    holdGain = null;
  }
}

function holdOff(): void {
  try {
    hold?.stop();
  } catch {
    /* уже остановлен */
  }
  try {
    hold?.disconnect();
  } catch {
    /* нет узла */
  }
  try {
    holdGain?.disconnect();
  } catch {
    /* нет узла */
  }
  hold = null;
  holdGain = null;
}

function watchPage(): void {
  if (watching || typeof document === 'undefined') return;
  watching = true;
  document.addEventListener('visibilitychange', () => {
    if (!shouldHold(useAlarm.getState().sound, useAlarm.getState().volume)) return;
    armSound();
  });
}

/**
 * Разбудить контекст и, если звук включён, не дать ему снова уснуть в фоне.
 * Звать из жеста (включение динамика) и при возврате на вкладку.
 */
export function armSound(): void {
  watchPage();
  const state = useAlarm.getState();
  if (!shouldHold(state.sound, state.volume)) {
    holdOff();
    return;
  }
  const ctx = context();
  if (!ctx) return;
  const go = (): void => {
    if (!shouldHold(useAlarm.getState().sound, useAlarm.getState().volume)) return;
    if (ctx.state === 'running') holdOn();
  };
  if (ctx.state === 'running') {
    go();
    return;
  }
  void ctx.resume?.().then(go).catch(() => {
    /* автоплей — тогда сыграет WAV */
  });
}

export function disarmSound(): void {
  holdOff();
}

function b64(bytes: Uint8Array): string {
  let raw = '';
  for (let i = 0; i < bytes.length; i += 1) raw += String.fromCharCode(bytes[i] ?? 0);
  return btoa(raw);
}

/**
 * Тот же пресет, что осциллятор, упакованный в WAV. Нужен, когда Web Audio
 * в фоне так и остался suspended: HTMLAudio.play() после жеста часто доходит.
 */
export function wavBeep(opts: BeepOpts = {}): string {
  const state = useAlarm.getState();
  const tone = toneOf(opts.tone ?? state.tone);
  const volume = volumeOf(opts.volume ?? state.volume);
  const scale = scaleOf(opts.scale ?? state.scale);
  const rub = opts.rub ?? 0;
  const key = `${tone}|${volume}|${scale}|${rub}`;
  const hit = wavCache.get(key);
  if (hit !== undefined) return hit;
  const peak = peakGain({ volume, tone, scale, rub });
  const spec = TONES[tone];
  const sr = 22050;
  const last = spec.hz.length > 0 ? (spec.hz.length - 1) * spec.gap + spec.dur : spec.dur;
  const n = Math.max(1, Math.ceil(sr * last));
  const samples = new Int16Array(n);
  spec.hz.forEach((hz, i) => {
    const start = Math.floor(sr * i * spec.gap);
    const len = Math.max(1, Math.floor(sr * spec.dur));
    for (let s = 0; s < len && start + s < n; s += 1) {
      const env = Math.sin(Math.PI * (s / len));
      const amp = Math.max(0, peak) * env;
      const at = start + s;
      samples[at] = (samples[at] ?? 0) + Math.round(amp * 32767 * Math.sin(2 * Math.PI * hz * (s / sr)));
    }
  });
  const bytes = new Uint8Array(44 + n * 2);
  const view = new DataView(bytes.buffer);
  const ascii = (at: number, text: string): void => {
    for (let i = 0; i < text.length; i += 1) bytes[at + i] = text.charCodeAt(i);
  };
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + n * 2, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sr, true);
  view.setUint32(28, sr * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  ascii(36, 'data');
  view.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i += 1) {
    view.setInt16(44 + i * 2, Math.max(-32767, Math.min(32767, samples[i] ?? 0)), true);
  }
  const uri = `data:audio/wav;base64,${b64(bytes)}`;
  wavCache.set(key, uri);
  return uri;
}

function playElem(opts: BeepOpts): void {
  try {
    if (typeof Audio === 'undefined') return;
    elem ??= new Audio();
    elem.src = wavBeep(opts);
    void elem.play()?.catch(() => {
      /* автоплей — молча */
    });
  } catch {
    /* нет элемента */
  }
}

function playOsc(ctx: AudioContext, opts: BeepOpts): void {
  const state = useAlarm.getState();
  const tone = toneOf(opts.tone ?? state.tone);
  const volume = volumeOf(opts.volume ?? state.volume);
  const scale = scaleOf(opts.scale ?? state.scale);
  const peak = peakGain({ volume, tone, scale, rub: opts.rub });
  if (peak <= 0) return;
  const now = ctx.currentTime;
  const spec = TONES[tone];
  spec.hz.forEach((hz, i) => {
    const at = i * spec.gap;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = spec.type;
    osc.frequency.value = hz;
    const start = now + at;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + Math.max(0.03, spec.dur - 0.02));
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + spec.dur);
  });
}

/**
 * Короткий тон из выбранного пресета. Не мелодия: звук должен быть узнаваем
 * боковым слухом и не раздражать на сотом повторении за смену.
 *
 * Если контекст не running (фон вкладки), не ставим осциллятор на мёртвые
 * часы — играем тот же тон файлом. Иначе человек услышит тишину, а потом,
 * вернувшись, запоздалый гудок.
 */
export function beep(opts: BeepOpts = {}): void {
  try {
    const state = useAlarm.getState();
    const peak = peakGain({
      volume: volumeOf(opts.volume ?? state.volume),
      tone: toneOf(opts.tone ?? state.tone),
      scale: scaleOf(opts.scale ?? state.scale),
      rub: opts.rub,
    });
    if (peak <= 0) return;
    const ctx = context();
    if (ctx === null) {
      playElem(opts);
      return;
    }
    const fire = (): void => {
      if (ctx.state === 'running') {
        playOsc(ctx, opts);
        if (shouldHold(useAlarm.getState().sound, useAlarm.getState().volume)) holdOn();
      } else {
        playElem(opts);
      }
    };
    if (ctx.state === 'running') {
      fire();
      return;
    }
    const next = ctx.resume?.();
    if (next !== undefined) void next.then(fire).catch(() => playElem(opts));
    else fire();
  } catch {
    // Звук — удобство; лента — работа. Отказ звука не имеет права уронить экран.
    playElem(opts);
  }
}

/**
 * Контекст создан. Услышать тон мы не можем — микрофона нет и не будет;
 * «звука нет» честно только когда браузер не дал даже AudioContext.
 */
export function soundTried(): boolean {
  return audio !== null;
}

if (typeof document !== 'undefined') {
  const unlock = (): void => {
    if (useAlarm.getState().sound) armSound();
  };
  document.addEventListener('pointerdown', unlock, { passive: true });
  document.addEventListener('keydown', unlock, { passive: true });
}

export interface Alert {
  id: string;
  title: string;
  body: string;
}

/**
 * Проверочное уведомление на первом заходе. В отличие от сигнала вилки,
 * его надо показать даже когда человек смотрит в ленту: иначе «проверили»
 * означает «разрешили», а не «дошло».
 */
export function notifyTest(): boolean {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
    new Notification('FORQ', {
      body: 'проверка: так придёт новая вилка, когда лента не на экране',
      tag: 'forq:permit-test',
      silent: true,
    });
    return true;
  } catch {
    return false;
  }
}

/** Уведомление во второй монитор. Тег по вилке: браузер сам не даст дубля. */
export function notifyOne(alert: Alert): void {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    // Уведомление не показывается, когда человек и так смотрит в ленту: сигнал
    // нужен тому, кто отвернулся.
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
    new Notification(alert.title, { body: alert.body, tag: alert.id, silent: true });
  } catch {
    // Тот же принцип, что и со звуком.
  }
}

/**
 * Чистая часть: какие строки нового кадра заслуживают сигнала.
 *
 * Возвращает и список сигналов, и обновлённую память — так её проверяет тест,
 * не поднимая браузер. `first` — первый кадр: он только запоминает.
 */
export function fresh(
  ids: readonly string[],
  known: ReadonlySet<string>,
  first: boolean,
): { ring: string[]; known: Set<string> } {
  const next = new Set(ids);
  if (first) return { ring: [], known: next };
  return { ring: ids.filter((id) => !known.has(id)), known: next };
}

/**
 * Ночная тишина, 02:00–09:00 по часам вкладки.
 *
 * Память при этом ведётся как обычно: проснувшийся в девять не должен получить
 * залп по всей ночной ленте, строк которой давно нет.
 */
export function hush(at: Date = new Date()): boolean {
  const h = at.getHours();
  return h >= 2 && h < 9;
}

/**
 * Подписка ленты на сигнал.
 *
 * Строки приходят УЖЕ отфильтрованными: профиль человека — это его фильтры и
 * его порог, и повторять их правила здесь значило бы завести вторую версию тех
 * же правил, которая однажды разойдётся с первой.
 *
 * Память ведётся ВСЕГДА, даже при выключенном звуке. Иначе включение
 * переключателя посреди смены выдало бы залп по всей текущей ленте.
 */
export function useRing(rows: readonly FeedRow[], gate: number, quiet = false): void {
  const sound = useAlarm((s) => s.sound);
  const unit = useShow((s) => s.unit);
  const bank = useSettings((s) => s.bank);
  const notify = useAlarm((s) => s.notify);
  const known = useRef<Set<string>>(new Set());
  const started = useRef(false);

  useEffect(() => {
    const worthy = rows.filter((row) => row.ghost === null && (TRANSFERS ? transferEligible(row.row) : gateProfit(row.row, unit, bank) >= gate));
    const { ring, known: next } = fresh(
      worthy.map((row) => row.id),
      known.current,
      !started.current,
    );
    known.current = next;
    started.current = true;
    if (ring.length === 0) return;
    if (quiet && hush()) return;
    const best = worthy
      .filter((row) => ring.includes(row.id))
      .reduce((a, b) => (a.row.profit >= b.row.profit ? a : b));
    if (sound) beep({ rub: best.row.profit });
    if (parked.browserPush || !notify) return;
    // Уведомление одно на кадр, даже когда строк пришло несколько: пять
    // всплывающих окон подряд человек закрывает не читая.
    notifyOne({
      id: best.id,
      title: profitText(
        best.row.profit,
        best.row.roiPct,
        useSettings.getState().fx,
        useShow.getState().unit,
      ),
      body:
        ring.length === 1
          ? `${best.row.roiPct.toFixed(2)} % · живёт ${best.age} с`
          : `и ещё ${ring.length - 1}, лучшая ${best.row.roiPct.toFixed(2)} %`,
    });
  }, [rows, gate, sound, notify, quiet, unit, bank]);
}
